import { Button, makeStyles, TextField } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import React, { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import theme from '../../theme';
import { Autocomplete } from '@material-ui/lab';
import AuditLogActions, { getActionName } from '../../constants/AuditLogActions';
import SearchIcon from '@material-ui/icons/Search';
import { Dayjs } from 'dayjs';

const useStyles = makeStyles({
  container: {
    gap: `${theme.spacing(1)}px`,
    padding: `${theme.spacing(2)}px`,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },

  actionFilter: {
    minWidth: '300px',
  },

  textFilter: {
    minWidth: '300px',
  },
});

const actions = Object.values(AuditLogActions);

export interface AuditLogSearchFilter {
  fromDate?: Date;
  toDate?: Date;
  action?: AuditLogActions | null;
  textCriteria?: string;
}

interface Props {
  onFilterChanged: (filter: AuditLogSearchFilter) => void;
}

export default function AuditLogFilter({ onFilterChanged }: Props) {
  const classes = useStyles();
  const [filter, setFilter] = useState<AuditLogSearchFilter>({});
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [action, setAction] = useState<AuditLogActions | undefined | null>(null);
  const [textCriteria, setTextCriteria] = useState<string>('');

  const [debouncedFilter] = useDebounce(filter, 300);

  useEffect(() => {
    if (debouncedFilter && onFilterChanged) {
      onFilterChanged(debouncedFilter);
    }
  }, [debouncedFilter, onFilterChanged]);

  useEffect(() => {
    setFilter((prevFilter) => {
      return { ...prevFilter, fromDate: fromDate?.toDate() };
    });
  }, [fromDate, setFilter]);

  useEffect(() => {
    setFilter((prevFilter) => {
      return { ...prevFilter, toDate: toDate?.toDate() };
    });
  }, [toDate, setFilter]);

  useEffect(() => {
    setFilter((prevFilter) => {
      return { ...prevFilter, action };
    });
  }, [action, setFilter]);

  useEffect(() => {
    setFilter((prevFilter) => {
      return { ...prevFilter, textCriteria: textCriteria ?? undefined };
    });
  }, [textCriteria, setFilter]);

  const onClear = () => {
    setFromDate(null);
    setToDate(null);
    setAction(null);
    setTextCriteria('');
  };

  return (
    <Box className={classes.container}>
      <SearchIcon />
      <TextField
        variant="outlined"
        className={classes.textFilter}
        name="textCriteria"
        label="User / Company"
        value={textCriteria}
        onChange={(e) => {
          setTextCriteria(e.target.value);
        }}
      />

      <Autocomplete
        options={actions}
        value={action}
        renderInput={(params) => <TextField {...params} label="Action type" variant="outlined" fullWidth />}
        onChange={(_, newValue) => {
          setAction(newValue ?? undefined);
        }}
        className={classes.actionFilter}
        getOptionLabel={getActionName}
      />
      <KeyboardDateTimePicker
        value={fromDate}
        maxDate={toDate ?? undefined}
        onChange={(date) => {
          setFromDate(date as Dayjs);
        }}
        inputVariant="outlined"
        format="DD/MM/YYYY HH:mm"
        label="From date"
        placeholder="From date"
        clearable
      />
      <KeyboardDateTimePicker
        value={toDate}
        minDate={fromDate ?? undefined}
        onChange={(date) => {
          setToDate(date as Dayjs);
        }}
        inputVariant="outlined"
        format="DD/MM/YYYY HH:mm"
        label="To date"
        placeholder="To date"
        clearable
      />
      <Button variant="contained" onClick={onClear} size="medium">
        Clear
      </Button>
    </Box>
  );
}
